import {
  Button,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import React, { Fragment, useContext, useState, useEffect } from "react";
import propTypes from "prop-types";

import IngestionListItem from "../../components/lists/listItems/IngestionListItem/IngestionListItem";
import { PagedDataView } from "../../components/lists/PagedDataView/PagedDataView";
import TabbedView from "../../components/lists/TabbedView/TabbedView";
import TabbedViewPage from "../../components/lists/TabbedView/TabbedViewPage";
import { ProgressDialog } from "../../components/notifications/progressDialog/progressDialog";
import ErrorDialog from "../../components/notifications/errorDialog/ErrorDialog";
import DateField from "../../components/editable/EditableField/fields/DateField";
import TimeField from "../../components/editable/EditableField/fields/TimeField";

import {
  IListIngestion,
  Ingestion,
  api,
  ListIngestionType,
  IReproccessIngestion,
} from "../../logic/api";

import { SessionContext } from "../../utility/useSession";
import { useDate } from "../../utility/useDate";

import flex from "../../styles/flex";
import { useTranslation } from "react-i18next";

const IngestionView: React.FunctionComponent<{
  clientCode?: string;
  bookingId?: number;
}> = (props) => {
  const { localizeDate } = useDate();

  const startDateDefault = new Date();
  startDateDefault.setHours(0);
  startDateDefault.setMinutes(0);
  startDateDefault.setSeconds(0);
  startDateDefault.setMilliseconds(0);

  const endDateDefault = new Date();
  endDateDefault.setHours(23);
  endDateDefault.setMinutes(59);
  endDateDefault.setSeconds(59);
  endDateDefault.setMilliseconds(999);

  const [startDate, setStartDate] = useState(localizeDate(startDateDefault).toISO() ?? "");
  const [endDate, setEndDate] = useState(localizeDate(endDateDefault).toISO() ?? "");
  const [retryQueue, setRetryQueue] = useState<IReproccessIngestion[]>([]);
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const retryTransactionRequest = api.hooks.admin.reprocessIngestionTransaction();
  const [filter, setFilter] = useState("");


  const [
    listIngestionParams,
    setListIngestionParams,
  ] = useState<IListIngestion>({
    type: ListIngestionType.all,
    clientCode: props.clientCode,
    startDate: startDate,
    endDate: endDate,
    bookingId: props.bookingId,
  });

  useEffect(() => {
    setListIngestionParams({ ...listIngestionParams, bookingId: props.bookingId, });
  }, [props.bookingId]);

  const retryTransactions = () => {
    const transactions = [...retryQueue];
    const retryNext = () => {
      const transaction = transactions[0];
      if (transaction == null) {
        setRetryQueue([]);
        setListIngestionParams({ ...listIngestionParams, type: 0 });
        return;
      }
      transactions.splice(0, 1);
      retryTransactionRequest.send(
        session.current.context,
        {
          ingestionId: transaction.ingestionId,
        },
        (result) => {
          if (result != null) {
            retryNext();
          }
        }
      );
    };
    retryNext();
  };

  return (
    <Fragment>
      <ProgressDialog status={retryTransactionRequest.status}></ProgressDialog>
      <ErrorDialog err={retryTransactionRequest.err}></ErrorDialog>
      <div style={flex.flexRows}>
        <div style={flex.flexFill}>
          <div style={{ textAlign: "left" }}>
            <div style={{ display: "inline-block" }}>
              <TabbedView
                selectedIndex={listIngestionParams.type}
                onSelectedIndexChange={(index) => {
                  setListIngestionParams({
                    ...listIngestionParams,
                    type: index,
                  });
                }}
              >
                <TabbedViewPage title={t("general.all")} />
                <TabbedViewPage title={t("mobile.errors")} />
                <TabbedViewPage title={t("mobile.processed")} />
              </TabbedView>
            </div>
          </div>
        </div>

        {props.bookingId == null && (
          <div style={flex.flexAuto}>
            <TextField
              autoFocus={false}
              label={t("home.search.searchFieldLabel")}
              variant="outlined"
              size="small"
              type="search"
              value={filter}
              onChange={(e) => {
                setFilter(e.currentTarget.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  if (e.currentTarget) {
                    e.currentTarget.blur();
                    setListIngestionParams({
                      ...listIngestionParams,
                      filter: filter,
                    });
                  }
                  return false;
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position={"end"}>
                    <IconButton
                      style={{ padding: 0 }}
                      onClick={() => {
                        setListIngestionParams({
                          ...listIngestionParams,
                          filter: filter,
                        });
                      }}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

        )}


        <div style={flex.flexAuto}>
          <div style={{ marginLeft: 10 }}>
            <Button
              variant="contained"
              onClick={() => {
                setListIngestionParams({ ...listIngestionParams });
              }}
            >
              {t("mobile.refresh")}
            </Button>
          </div>
        </div>
        <div style={flex.flexAuto}>
          {retryQueue.length > 0 && (
            <div style={{ marginLeft: 10 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  retryTransactions();
                }}
              >
                {t("mobile.retryTransactions", { length: retryQueue.length })}
              </Button>
            </div>
          )}
        </div>
      </div>
      {props.bookingId == null && (

        <div style={flex.flexRows}>
          <div style={flex.flexFill}>
          </div>
          <div style={flex.flexAuto}>
            <div style={{ padding: 5 }}>
              <DateField label={t("booking.labels.startDate")} value={startDate} onChange={(date) => {
                setStartDate(date);
                setListIngestionParams({ ...listIngestionParams, startDate: date });
              }}></DateField>
            </div>
          </div>
          <div style={flex.flexAuto}>
            <div style={{ padding: 5 }}>
              <TimeField fiveMinutes={false} label={t("kmos.labels.startTime")} value={startDate} onChange={(date) => {
                setStartDate(date);
                setListIngestionParams({ ...listIngestionParams, startDate: date });
              }}></TimeField>
            </div>

          </div>
          <div style={flex.flexAuto}>
            <div style={{ padding: 5 }}>
              <DateField label={t("booking.labels.endDate")} value={endDate} onChange={(date) => {
                setEndDate(date);
                setListIngestionParams({ ...listIngestionParams, endDate: date });
              }}></DateField>
            </div>
          </div>
          <div style={flex.flexAuto}>
            <div style={{ padding: 5 }}>
              <TimeField fiveMinutes={false} label={t("kmos.labels.endTime")} value={endDate} onChange={(date) => {
                setEndDate(date);
                setListIngestionParams({ ...listIngestionParams, endDate: date });
              }}></TimeField>
            </div>
          </div>

        </div>


      )}
      <div style={{ minHeight: 300 }}>
        <PagedDataView<IListIngestion, Ingestion>
          apiHook={api.hooks.admin.listIngestionTransactions}
          parameters={listIngestionParams}
          renderItem={(item) => {
            const retryIndex = retryQueue.findIndex((tran) => {
              return tran.ingestionId == item.id;
            });
            const isSelectedForRetry = retryIndex != -1;

            return (
              <IngestionListItem
                selectedForRetry={isSelectedForRetry}
                ingestion={item}
                onClick={() => {
                  console.log(item);
                }}
                onRetry={() => {
                  if (isSelectedForRetry == true) {
                    const queue = [...retryQueue];
                    queue.splice(retryIndex, 1);
                    setRetryQueue(queue);
                    return;
                  }

                  setRetryQueue([
                    ...retryQueue,
                    ...[
                      { ingestionId: item.id },
                    ],
                  ]);
                }}
              ></IngestionListItem>
            );
          }}
        >
          <div style={{ padding: 20, backgroundColor: "#eee" }}>
            {t("mobile.noTransactionsFound")}
          </div>
        </PagedDataView>
      </div>
    </Fragment>
  );
};

IngestionView.propTypes = {
  clientCode: propTypes.string,
  bookingId: propTypes.number
};
export default IngestionView;
